import React from 'react';
import {
    BoundingBoxData,
    EventPlaybackRange,
    HighlightRegion,
    HighlightType,
    TimeDomain,
} from '@/types/video-player';
import HlsVideoElement from 'hls-video-element';
import {
    hasModifierKeyPressed,
    isEditableOrInteractiveTarget,
} from '@/utils/keyboard';

export const PLAYBACK_SPEEDS = [0.25, 0.5, 1, 1.25, 1.5, 2, 4];

export const PROGRESS_THUMBNAIL_HEIGHT = 90;
export const PROGRESS_THUMBNAIL_WIDTH = 160;
export const PROGRESS_THUMBNAIL_PADDING = 8;

export function getVideoElementFromRef(
    ref: React.RefObject<any>,
): HTMLVideoElement | HlsVideoElement | null {
    if (!ref.current) return null;

    if (ref.current?.getInternalPlayer) {
        return ref.current.getInternalPlayer();
    }

    if (ref.current instanceof HlsVideoElement) {
        return ref.current;
    }

    return ref.current;
}

export function computeObjectFitContainLayout(videoElement: HTMLVideoElement): {
    renderedWidth: number;
    renderedHeight: number;
    offsetX: number;
    offsetY: number;
} {
    const videoWidth = videoElement.videoWidth || 1;
    const videoHeight = videoElement.videoHeight || 1;
    const rect = videoElement.getBoundingClientRect();
    const containerWidth = rect.width;
    const containerHeight = rect.height;

    const videoAspect = videoWidth / videoHeight;
    const containerAspect = containerWidth / containerHeight;

    if (videoAspect > containerAspect) {
        const renderedWidth = containerWidth;
        const renderedHeight = containerWidth / videoAspect;
        return {
            renderedWidth,
            renderedHeight,
            offsetX: 0,
            offsetY: (containerHeight - renderedHeight) / 2,
        };
    }

    const renderedHeight = containerHeight;
    const renderedWidth = containerHeight * videoAspect;
    return {
        renderedWidth,
        renderedHeight,
        offsetX: (containerWidth - renderedWidth) / 2,
        offsetY: 0,
    };
}

export function getEventHighlight(
    range: EventPlaybackRange,
    id: string,
): HighlightRegion {
    return {
        id,
        startTime: range.start,
        endTime: range.end,
        type: HighlightType.EVENT,
        domain: range.domain,
    };
}

export function getFocusHighlights(
    boundingBoxProps: BoundingBoxData,
    domain: TimeDomain,
): HighlightRegion[] {
    const { frames, focuses } = boundingBoxProps;

    if (focuses.length === 0 || frames.length === 0) {
        return [];
    }

    return focuses
        .map((focus, index) => {
            const startFrameId = focus[0];
            const endFrameId = focus[focus.length - 1];

            if (startFrameId === undefined || endFrameId === undefined) {
                return null;
            }

            const startFrame = frames.find(
                (frame) => frame.id === startFrameId,
            );
            const endFrame = frames.find((frame) => frame.id === endFrameId);

            if (!startFrame || !endFrame) {
                return null;
            }

            return {
                id: `focus-${index}`,
                startTime: startFrame.time,
                endTime: endFrame.time,
                type: HighlightType.FOCUS,
                domain,
            };
        })
        .filter((h): h is HighlightRegion => h !== null);
}

export function clampTooltipPosition(
    mouseX: number,
    tooltipWidth: number,
    containerWidth: number,
    padding: number,
): number {
    const centered = mouseX - tooltipWidth / 2;
    const maxLeft = containerWidth - tooltipWidth - padding;
    return Math.max(padding, Math.min(maxLeft, centered));
}

const hiddenVideoCache = new Map<string, HTMLVideoElement>();
const videoLoadPromises = new Map<string, Promise<void>>();

const createHiddenVideo = (src: string): Promise<HTMLVideoElement> => {
    return new Promise((resolve, reject) => {
        const hiddenVideo = document.createElement('video');
        hiddenVideo.src = src;
        hiddenVideo.crossOrigin = 'use-credentials';
        hiddenVideo.muted = true;
        hiddenVideo.preload = 'auto';
        hiddenVideo.style.position = 'fixed';
        hiddenVideo.style.top = '-9999px';
        hiddenVideo.style.left = '-9999px';
        hiddenVideo.style.width = '1px';
        hiddenVideo.style.height = '1px';
        hiddenVideo.style.opacity = '0';
        hiddenVideo.style.pointerEvents = 'none';

        let resolved = false;

        const onCanPlay = () => {
            if (!resolved && hiddenVideo.readyState >= 3) {
                resolved = true;
                cleanup();
                resolve(hiddenVideo);
            }
        };

        const onError = (error: Event) => {
            cleanup();
            console.error('Hidden video load error:', error);
            reject(new Error('Failed to load hidden video'));
        };

        const cleanup = () => {
            hiddenVideo.removeEventListener('canplay', onCanPlay);
            hiddenVideo.removeEventListener('canplaythrough', onCanPlay);
            hiddenVideo.removeEventListener('error', onError);
        };

        hiddenVideo.addEventListener('canplay', onCanPlay);
        hiddenVideo.addEventListener('canplaythrough', onCanPlay);
        hiddenVideo.addEventListener('error', onError);

        document.body.appendChild(hiddenVideo);

        hiddenVideo.load();

        setTimeout(() => {
            if (!resolved) {
                cleanup();
                reject(new Error('Hidden video load timeout'));
            }
        }, 10000);
    });
};

const getOrCreateHiddenVideo = async (
    src: string,
): Promise<HTMLVideoElement> => {
    if (hiddenVideoCache.has(src)) {
        return hiddenVideoCache.get(src)!;
    }

    if (videoLoadPromises.has(src)) {
        await videoLoadPromises.get(src);
        return hiddenVideoCache.get(src)!;
    }

    const loadPromise = createHiddenVideo(src)
        .then((video) => {
            hiddenVideoCache.set(src, video);
            videoLoadPromises.delete(src);
            return video;
        })
        .catch((error) => {
            videoLoadPromises.delete(src);
            throw error;
        });

    videoLoadPromises.set(
        src,
        loadPromise.then(() => {}),
    );
    return loadPromise;
};

const FRAME_SEEK_TIMEOUT_MS = 15000;
const SEEK_POSITION_EPSILON_SECONDS = 0.05;

const isFrameDataReady = (video: HTMLVideoElement): boolean =>
    video.readyState >= video.HAVE_CURRENT_DATA &&
    video.videoWidth > 0 &&
    video.videoHeight > 0;

const isAtRequestedFrame = (video: HTMLVideoElement, time: number): boolean =>
    Math.abs(video.currentTime - time) < SEEK_POSITION_EPSILON_SECONDS;

export const extractVideoFrame = async (
    videoElement: HTMLVideoElement,
    time: number,
): Promise<string> => {
    const hiddenVideo = await getOrCreateHiddenVideo(videoElement.src);

    const canvas = document.createElement('canvas');
    const width = hiddenVideo.videoWidth || 640;
    const height = hiddenVideo.videoHeight || 360;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
        throw new Error('Could not get canvas context');
    }

    const drawCurrentFrame = (): string => {
        ctx.drawImage(hiddenVideo, 0, 0, width, height);
        return canvas.toDataURL('image/jpeg', 0.75);
    };

    // Already positioned on the requested frame: setting `currentTime` to the
    // same value never fires `seeked`, so capture now instead of waiting for an
    // event that will never arrive.
    if (
        isFrameDataReady(hiddenVideo) &&
        isAtRequestedFrame(hiddenVideo, time)
    ) {
        return drawCurrentFrame();
    }

    return new Promise<string>((resolve, reject) => {
        let settled = false;

        const cleanup = () => {
            clearTimeout(timeoutId);
            hiddenVideo.removeEventListener('seeked', onSeeked);
            hiddenVideo.removeEventListener('error', onError);
        };

        const onSeeked = () => {
            // Double rAF so the decoded frame is presented before we sample it.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (settled) return;
                    settled = true;
                    try {
                        const dataUrl = drawCurrentFrame();
                        cleanup();
                        resolve(dataUrl);
                    } catch (error) {
                        cleanup();
                        reject(error);
                    }
                });
            });
        };

        const onError = () => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(new Error('Failed to load video frame'));
        };

        // The requested slice may not be buffered yet, so a seek can trigger a
        // fresh range request. Allow enough time for that download to land
        // before giving up rather than failing while it is still in flight.
        const timeoutId = setTimeout(() => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(new Error('Thumbnail extraction timeout'));
        }, FRAME_SEEK_TIMEOUT_MS);

        hiddenVideo.addEventListener('seeked', onSeeked);
        hiddenVideo.addEventListener('error', onError);

        try {
            hiddenVideo.currentTime = time;
        } catch (error) {
            settled = true;
            cleanup();
            reject(error);
        }
    });
};

export const roundTimestamp = (
    time: number,
    precision: number = 0.5,
): number => {
    return Math.round(time / precision) * precision;
};

export interface VideoFrameRegion {
    sourceX: number;
    sourceY: number;
    sourceWidth: number;
    sourceHeight: number;
}

export interface VideoElementRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * Convert a rectangle expressed in the video element's local CSS coordinates
 * into a native source-pixel region, clamped to the letterbox-corrected
 * rendered video area.
 *
 * Shared by the cropper (user selection) and the magnifier (cursor-centered
 * sample) so the object-fit layout, source scaling, and bounds clamping live
 * in a single place and cannot drift between the two features.
 *
 * Returns `null` only when the region genuinely cannot be computed:
 * - video metadata is not available yet (intrinsic size still unknown);
 * - the rendered area is non-positive;
 * - the rectangle collapses to nothing after clamping (no overlap with the
 *   visible video).
 * A non-empty rectangle that maps to a sub-pixel source span is preserved as a
 * minimum 1px region rather than being dropped.
 */
export function videoElementRectToFrameRegion(
    videoElement: HTMLVideoElement,
    rect: VideoElementRect,
): VideoFrameRegion | null {
    const { videoWidth, videoHeight } = videoElement;
    if (!videoWidth || !videoHeight) return null;

    const { renderedWidth, renderedHeight, offsetX, offsetY } =
        computeObjectFitContainLayout(videoElement);

    if (renderedWidth <= 0 || renderedHeight <= 0) return null;

    const clampToRange = (value: number, min: number, max: number): number =>
        Math.max(min, Math.min(value, max));

    const right = offsetX + renderedWidth;
    const bottom = offsetY + renderedHeight;
    const left = clampToRange(rect.left, offsetX, right);
    const top = clampToRange(rect.top, offsetY, bottom);
    const farRight = clampToRange(rect.left + rect.width, offsetX, right);
    const farBottom = clampToRange(rect.top + rect.height, offsetY, bottom);

    // The selection lies entirely outside the visible video.
    if (farRight <= left || farBottom <= top) return null;

    const scaleX = videoWidth / renderedWidth;
    const scaleY = videoHeight / renderedHeight;

    // Clamp the rounded origin so at least one pixel remains, then cap the size
    // to the frame pixels still available from that origin. Rounding origin and
    // size independently can otherwise push sourceX + sourceWidth past the
    // frame, making drawImage clip the source rect into a blank/partial result
    // at the right/bottom edge.
    const sourceX = clampToRange(
        Math.round((left - offsetX) * scaleX),
        0,
        videoWidth - 1,
    );
    const sourceY = clampToRange(
        Math.round((top - offsetY) * scaleY),
        0,
        videoHeight - 1,
    );

    return {
        sourceX,
        sourceY,
        sourceWidth: clampToRange(
            Math.round((farRight - left) * scaleX),
            1,
            videoWidth - sourceX,
        ),
        sourceHeight: clampToRange(
            Math.round((farBottom - top) * scaleY),
            1,
            videoHeight - sourceY,
        ),
    };
}

const loadImageFromDataUrl = (dataUrl: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () =>
            reject(new Error('Failed to load extracted frame'));
        image.src = dataUrl;
    });

const tryDirectDrawAndExport = (
    videoElement: HTMLVideoElement,
    region: VideoFrameRegion,
): string | null => {
    const canvas = document.createElement('canvas');
    canvas.width = region.sourceWidth;
    canvas.height = region.sourceHeight;
    const context = canvas.getContext('2d');
    if (!context) return null;
    try {
        context.drawImage(
            videoElement,
            region.sourceX,
            region.sourceY,
            region.sourceWidth,
            region.sourceHeight,
            0,
            0,
            region.sourceWidth,
            region.sourceHeight,
        );
        return canvas.toDataURL('image/png');
    } catch {
        return null;
    }
};

export const extractCroppedVideoFrame = async (
    videoElement: HTMLVideoElement,
    region: VideoFrameRegion,
): Promise<string> => {
    const direct = tryDirectDrawAndExport(videoElement, region);
    if (direct) return direct;

    const fullFrameDataUrl = await extractVideoFrame(
        videoElement,
        videoElement.currentTime,
    );
    const fullFrameImage = await loadImageFromDataUrl(fullFrameDataUrl);

    const sourceWidth = videoElement.videoWidth || fullFrameImage.naturalWidth;
    const sourceHeight =
        videoElement.videoHeight || fullFrameImage.naturalHeight;
    const horizontalRatio = fullFrameImage.naturalWidth / sourceWidth;
    const verticalRatio = fullFrameImage.naturalHeight / sourceHeight;

    const canvas = document.createElement('canvas');
    canvas.width = region.sourceWidth;
    canvas.height = region.sourceHeight;
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Could not get canvas context');
    }
    context.drawImage(
        fullFrameImage,
        region.sourceX * horizontalRatio,
        region.sourceY * verticalRatio,
        region.sourceWidth * horizontalRatio,
        region.sourceHeight * verticalRatio,
        0,
        0,
        region.sourceWidth,
        region.sourceHeight,
    );
    return canvas.toDataURL('image/png');
};

export const dataUrlToBlob = (dataUrl: string): Blob => {
    const [header, base64Payload] = dataUrl.split(',');
    const mimeType = header.match(/:(.*?);/)?.[1] ?? 'image/png';
    const binary = atob(base64Payload);
    const bytes = Uint8Array.from(binary, (character) =>
        character.charCodeAt(0),
    );
    return new Blob([bytes], { type: mimeType });
};

export const downloadDataUrlAsFile = (
    dataUrl: string,
    fileName: string,
): void => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export function calculateSkipPosition(
    currentPosition: number,
    skipSeconds: number,
    earliestTimestamp: number | null,
    currentTimestamp: number,
): number {
    const newPosition = currentPosition + skipSeconds;
    const minTimestamp = earliestTimestamp ?? 0;
    return Math.min(currentTimestamp, Math.max(minTimestamp, newPosition));
}

export function createPlayerKeyHandler({
    live,
    togglePlay,
    skip,
    skipSeconds,
    zoomed,
    onToggleZoom,
    onExitZoom,
    onToggleZoomLock,
    onToggleCrop,
    onToggleLoop,
}: {
    live: boolean;
    togglePlay: () => void;
    skip: (seconds: number) => void;
    skipSeconds: number;
    zoomed?: boolean;
    onToggleZoom?: () => void;
    onExitZoom?: () => void;
    onToggleZoomLock?: () => void;
    onToggleCrop?: () => void;
    onToggleLoop?: () => void;
}): (e: KeyboardEvent) => void {
    return (e: KeyboardEvent) => {
        if (isEditableOrInteractiveTarget(e) || hasModifierKeyPressed(e)) {
            return;
        }

        switch (e.code) {
            case 'Space':
                e.preventDefault();
                togglePlay();
                break;
            case 'ArrowLeft':
                if (live) return;
                e.preventDefault();
                skip(-skipSeconds);
                break;
            case 'ArrowRight':
                if (live) return;
                e.preventDefault();
                skip(skipSeconds);
                break;
            case 'KeyZ':
                if (!onToggleZoom) return;
                e.preventDefault();
                onToggleZoom();
                break;
            case 'KeyC':
                if (!onToggleCrop) return;
                e.preventDefault();
                onToggleCrop();
                break;
            case 'KeyL':
                if (zoomed && onToggleZoomLock) {
                    e.preventDefault();
                    onToggleZoomLock();
                    break;
                }
                if (live || !onToggleLoop) return;
                e.preventDefault();
                onToggleLoop();
                break;
            case 'Escape':
                if (!zoomed || !onExitZoom) return;
                e.preventDefault();
                onExitZoom();
                break;
        }
    };
}
